import { Modal } from "melodies-source/Modal";
import { ReactComponent as SvgLinkAlt } from "assets/svg/link-alt.svg";
import { Body1, H2, H4 } from "melodies-source/Text/index";
import styled from "styled-components";
import { Button } from "melodies-source/Button";
import { useState } from "react";
import { SvgCheck } from "melodies-source/Svgs/Check";

export const GetLinkModal = ({
  isOpen,
  onClose,
  surveyId,
  featuredUrl,
}: {
  isOpen: boolean;
  onClose: VoidFunction;
  surveyId: string;
  featuredUrl?: string;
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkFeaturedCopied, setLinkFeaturedCopied] = useState(false);
  const [surveyLink, setSurveyLink] = useState(
    `${process.env.REACT_APP_SET_FAN_URL}/${surveyId}`
  );
  const onCopyLink = () => {
    navigator.clipboard.writeText(surveyLink);
    setLinkCopied(true);
  };

  const onCopyFeaturedLink = () => {
    navigator.clipboard.writeText(featuredUrl);
    setLinkFeaturedCopied(true);
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={() => {
        setLinkCopied(false);
        setLinkFeaturedCopied(false);
        onClose();
      }}
      withCloseIcon={false}
      header={
        <LinkContainer>
          <SvgLinkAlt />
        </LinkContainer>
      }
    >
      <ModalBody>
        <H2 style={{ width: 312, textAlign: "center" }}>
          Share the link for this SET.Fan experience
        </H2>
        <CopyLinkContainer>
          <DisabledInput>
            <SurveyLinkText>{surveyLink}</SurveyLinkText>
          </DisabledInput>
          <CopyLinkButton
            disabled={linkCopied}
            onClick={onCopyLink}
            leftIcon={linkCopied ? <SvgCheck /> : undefined}
          >
            {linkCopied ? "Copied" : "Copy Link"}
          </CopyLinkButton>
        </CopyLinkContainer>
        {featuredUrl && (
          <>
            <Body1 style={{ textAlign: "center" }}>OR</Body1>
            <CopyLinkContainer>
              <DisabledInput style={{ width: "100%" }}>
                <SurveyLinkText>{featuredUrl}</SurveyLinkText>
              </DisabledInput>
              <CopyLinkButton
                disabled={linkFeaturedCopied}
                onClick={onCopyFeaturedLink}
                leftIcon={linkFeaturedCopied ? <SvgCheck /> : undefined}
              >
                {linkFeaturedCopied ? "Copied" : "Copy Link"}
              </CopyLinkButton>
            </CopyLinkContainer>
          </>
        )}
        <CloseButton
          onClick={() => {
            setLinkCopied(false);
            setLinkFeaturedCopied(false);
            onClose();
          }}
        >
          Close
        </CloseButton>
      </ModalBody>
    </StyledModal>
  );
};

export const GetFeaturedLinkModal = ({
  isOpen,
  onClose,
  link,
  title,
}: {
  isOpen: boolean;
  onClose: VoidFunction;
  link: string;
  title: string;
}) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const onCopyLink = () => {
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={() => {
        setLinkCopied(false);
        onClose();
      }}
      withCloseIcon={false}
      header={
        <LinkContainer>
          <SvgLinkAlt />
        </LinkContainer>
      }
    >
      <ModalBody>
        <H4 style={{ textAlign: "center" }}>Featured Survey</H4>
        <H2 style={{ width: 312, textAlign: "center" }}>{title}</H2>
        <CopyLinkContainer>
          <DisabledInput style={{ width: "100%" }}>
            <SurveyLinkText>{link}</SurveyLinkText>
          </DisabledInput>
          <CopyLinkButton
            disabled={linkCopied}
            onClick={onCopyLink}
            leftIcon={linkCopied ? <SvgCheck /> : undefined}
          >
            {linkCopied ? "Copied" : "Copy Link"}
          </CopyLinkButton>
        </CopyLinkContainer>
        <CloseButton
          onClick={() => {
            setLinkCopied(false);
            onClose();
          }}
        >
          Close
        </CloseButton>
      </ModalBody>
    </StyledModal>
  );
};

export const ConfirmFeatureModal = ({
  isOpen,
  onClose,
  onConfirm,
  link,
  currentTitle,
}: {
  isOpen: boolean;
  onClose: VoidFunction;
  onConfirm: VoidFunction;
  link: string;
  currentTitle?: string;
}) => {
  return (
    <StyledModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
      }}
      withCloseIcon={false}
      header={`Feature this survey on ${link}?`}
    >
      <ModalBody>
        {currentTitle ? (
          <>
            <Body1 style={{ marginBottom: "12px" }}>
              {link} is currently point to your <b>{currentTitle}</b> survey.
            </Body1>
            <Body1>
              Confirming will point {link} to this survey instead. The previous
              survey will still be accessible via its unique link.
            </Body1>
          </>
        ) : (
          <Body1>{link} is not currently pointing to any survey.</Body1>
        )}
        <div
          style={{
            display: "flex",
            marginTop: "20px",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Confirm
          </Button>
        </div>
      </ModalBody>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  div:first-child {
    margin-left: auto;
    margin-right: auto;
  }
`;

const CopyLinkContainer = styled.div`
  display: flex;
  width: 380px;
  margin-top: 30px;
  margin-bottom: 24px;
`;

const CopyLinkButton = styled(Button)<{ disabled: boolean }>`
  width: 123px;
  border-top-left-radius: 0px;
  border-bottom-left-radius: 0px;
  padding: ${({ disabled }) =>
    disabled ? "10px 20px 10px 12px" : "10px 14px"};
`;

const CloseButton = styled.div`
  display: flex;
  width: 114px;
  height: 56px;
  flex-direction: column;
  justify-content: center;
  color: #1b0076;
  cursor: pointer;
  text-align: center;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
`;

const DisabledInput = styled.div`
  height: 44px;
  border-radius: 6px 0 0 6px;
  border: 1px solid #ccc;
  background: #fff;
  padding: 11px 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SurveyLinkText = styled.span`
  color: black;
  font-family: Arial;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 25px;
`;

const LinkContainer = styled.div`
  width: 72px;
  height: 72px;
  background-color: #f2f5f7;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
